import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useMantineColorScheme } from '@mantine/core';

export type ThemeName = 'system' | 'light' | 'dark' | 'blue' | 'green' | 'purple' | 'orange';

interface ThemeContextType {
    themeName: ThemeName;
    setThemeName: (theme: ThemeName) => void;
    isDarkMode: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

interface ThemeProviderProps {
    children: ReactNode;
}

/** Syncs ThemeContext's themeName to Mantine's color scheme. Must be rendered inside MantineProvider. */
function ThemeSync({ themeName, isDarkMode }: { themeName: ThemeName; isDarkMode: boolean }) {
    const { setColorScheme } = useMantineColorScheme();
    useEffect(() => {
        if (themeName === 'system') {
            setColorScheme('auto');
        } else if (themeName === 'dark' || themeName === 'light') {
            setColorScheme(themeName);
        } else {
            // blue, green, purple, orange - use light/dark from isDarkMode
            setColorScheme(isDarkMode ? 'dark' : 'light');
        }
        // Intentionally omit setColorScheme: it can get a new reference when Mantine re-renders after
        // the update, causing this effect to re-run and loop (stuck pulsating UI after theme change).
    }, [themeName, isDarkMode]);
    return null;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
    const [themeName, setThemeNameState] = useState<ThemeName>(() => {
        const saved = localStorage.getItem('theme-preference');
        return (saved as ThemeName) || 'system';
    });

    const [systemDarkMode, setSystemDarkMode] = useState(() => {
        return window.matchMedia('(prefers-color-scheme: dark)').matches;
    });

    const setThemeName = (theme: ThemeName) => {
        setThemeNameState(theme);
        localStorage.setItem('theme-preference', theme);
    };

    // Listen for system theme changes
    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (e: MediaQueryListEvent) => {
            setSystemDarkMode(e.matches);
        };

        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, []);

    // Determine if we should use dark mode
    const isDarkMode = themeName === 'system' ? systemDarkMode : themeName === 'dark';

    const value: ThemeContextType = {
        themeName,
        setThemeName,
        isDarkMode,
    };

    return (
        <ThemeContext.Provider value={value}>
            <ThemeSync themeName={themeName} isDarkMode={isDarkMode} />
            {children}
        </ThemeContext.Provider>
    );
};
